import React, { forwardRef } from 'react'
import * as Headless from '@headlessui/react'
import { classNames } from '@/Utils/Utils'
import { Link } from '@/Components/Navigation/Link'
import { InertiaLinkProps } from '@inertiajs/react'

const styles = {
    base: 'relative inline-flex items-center tracking-wide rounded-md font-semibold focus:outline-none no-underline focus:ring-2 focus:ring-stone-400 focus:ring-offset-2 transition ease-in-out duration-150 disabled:cursor-default!',

    primary: {
        solid: 'bg-stone-800 text-stone-50 border border-transparent hover:bg-stone-900 data-disabled:opacity-50 hover:text-white',
        outline: 'bg-white border border-stone-800 text-stone-800 hover:bg-stone-50 data-disabled:bg-stone-100',
    },
    secondary: {
        solid: 'bg-stone-200 text-stone-800 border border-transparent hover:bg-stone-300 data-disabled:opacity-50 hover:text-stone-900',
        outline:
            'bg-white border border-stone-300 text-stone-700 hover:bg-stone-100 hover:text-stone-800 hover:border-stone-300 data-disabled:bg-stone-100',
    },
    danger: {
        solid: 'bg-red-600 text-white border border-transparent hover:bg-red-500 data-disabled:opacity-50 hover:text-red-50',
        outline: 'bg-white border border-stone-300 text-stone-700 hover:bg-red-800 hover:border-red-800 hover:text-red-50 data-disabled:bg-red-100',
    },
    secondaryDanger: {
        solid: 'bg-stone-200 text-stone-800 border border-transparent hover:bg-red-800 data-disabled:opacity-50 hover:text-red-50',
        outline: 'bg-white border border-stone-300 text-stone-700 hover:bg-red-800 hover:text-red-50 hover:border-red-800 data-disabled:bg-red-100',
        plain: 'bg-transparent border border-transparent text-stone-700 hover:bg-red-800 hover:text-red-50 hover:border-red-800 data-disabled:opacity-50',
    },
    plain: 'text-gray-400 hover:text-gray-800 data-disabled:opacity-50 focus:ring-0',
    link: 'text-stone-800 hover:text-stone-900 data-disabled:opacity-50 hover:bg-stone-300',
    size: {
        sm: 'px-2.5 text-sm py-0.5',
        md: 'px-4 text-sm py-1.5',
        lg: 'px-6 text-base py-2',
    },
}

type ButtonProps = {
    outline?: boolean
    plain?: boolean
    download?: boolean | string
    className?: string
    href?: string
    externalLink?: boolean
    method?: string
    children: React.ReactNode
    size?: 'sm' | 'md' | 'lg'
} & Omit<Headless.ButtonProps, 'className'>

const Button = forwardRef(function Button(
    { type, outline, className, externalLink, children, size, ...props }: ButtonProps,
    ref: React.ForwardedRef<HTMLElement>
) {
    let classes = classNames(className, styles.size[size || 'md'])

    return 'href' in props ? (
        <>
            {externalLink ? (
                <a {...(props as React.ComponentPropsWithoutRef<'a'>)} className={classes} ref={ref as React.ForwardedRef<HTMLAnchorElement>}>
                    {children}
                </a>
            ) : (
                <Link
                    {...(props as InertiaLinkProps & React.ComponentPropsWithoutRef<'a'>)}
                    className={classes}
                    ref={ref as React.ForwardedRef<HTMLAnchorElement>}
                >
                    <TouchTarget>{children}</TouchTarget>
                </Link>
            )}
        </>
    ) : (
        <Headless.Button {...props} type={type || 'submit'} className={classes} ref={ref}>
            <TouchTarget>{children}</TouchTarget>
        </Headless.Button>
    )
})

export const PrimaryButton = forwardRef(function PrimaryButton(
    { outline, className, externalLink, ...props }: ButtonProps,
    ref: React.ForwardedRef<HTMLButtonElement>
) {
    let classes = classNames(className, styles.base, outline ? styles.primary.outline : styles.primary.solid)

    return (
        <Button {...props} externalLink={externalLink} className={classes} ref={ref}>
            {props.children}
        </Button>
    )
})

export const SecondaryButton = forwardRef(function SecondaryButton(
    { outline, className, ...props }: ButtonProps,
    ref: React.ForwardedRef<HTMLButtonElement>
) {
    let classes = classNames(className, styles.base, outline ? styles.secondary.outline : styles.secondary.solid)

    return (
        <Button {...props} className={classes} ref={ref}>
            {props.children}
        </Button>
    )
})

export const SecondaryDangerButton = forwardRef(function SecondaryDangerButton(
    { outline, plain, className, ...props }: ButtonProps,
    ref: React.ForwardedRef<HTMLButtonElement>
) {
    let classes = classNames(
        className,
        styles.base,
        outline ? styles.secondaryDanger.outline : plain ? styles.secondaryDanger.plain : styles.secondaryDanger.solid
    )

    return (
        <Button {...props} className={classes} ref={ref}>
            {props.children}
        </Button>
    )
})

export const LinkButton = forwardRef(function SecondaryButton(
    { outline, className, ...props }: ButtonProps,
    ref: React.ForwardedRef<HTMLButtonElement>
) {
    let classes = classNames(className, styles.base, styles.link)

    return (
        <Button {...props} className={classes} ref={ref}>
            {props.children}
        </Button>
    )
})

export const PlainButton = forwardRef(function PlainButton(
    { outline, className, ...props }: ButtonProps,
    ref: React.ForwardedRef<HTMLButtonElement>
) {
    let classes = classNames(className, styles.plain)

    return (
        <Button {...props} className={classes} ref={ref}>
            {props.children}
        </Button>
    )
})

export const DangerButton = forwardRef(function DangerButton(
    { outline, className, ...props }: ButtonProps,
    ref: React.ForwardedRef<HTMLButtonElement>
) {
    let classes = classNames(className, styles.base, outline ? styles.danger.outline : styles.danger.solid)

    return (
        <Button {...props} className={classes} ref={ref}>
            {props.children}
        </Button>
    )
})

/**
 * Expand the hit area to at least 44×44px on touch devices
 */
export function TouchTarget({ children }: { children: React.ReactNode }) {
    return (
        <>
            <span
                className="absolute top-1/2 left-1/2 size-[max(100%,2.75rem)] -translate-x-1/2 -translate-y-1/2 pointer-fine:hidden"
                aria-hidden="true"
            />
            {children}
        </>
    )
}
